import React, { useEffect, useRef, useState } from "react";
import "./ColumnsWrapper.css"
import Columns from "./Columns";
import {
    getLanguageName,
    getOrderedWordCount,
    getWordAtOrderedIndex,
    getWordsAtOrderedIndices,
    languageCodes,
} from "../data/language_lists.js";
import {
    WIKDICT_CHUNK_METADATA_BY_LANGUAGE,
    WIKDICT_ENGLISH_PAIRS_BY_LANGUAGE,
    WIKDICT_EXTRA_CHUNK_SIZE,
    WIKDICT_SUPPORTED_LANGUAGE_CODES,
} from "../data/wikdictEnglishPairs.js";
import { WORD_CATEGORIES, WORD_CATEGORY_TREE, getCategoryLabel } from "../data/wordCategoryTaxonomy.js";
import { loadCachedWikDictChunk } from "../utils/wikdictChunkCache.js";

const ABSOLUTE_MAX_WORDS_TO_GUESS = 10;
export const PRELOADED_WORD_LIMIT = 2500;
const DEFAULT_WORDS_TO_GUESS = 5;
const DEFAULT_WORDS_TO_DRAW_FROM = 100;
const MIN_WORDS_TO_DRAW_FROM = 5;
const MIN_WORDS_TO_GUESS = 2;
const INITIAL_DRAW_MULTIPLIER = 1;
const MIN_DRAW_MULTIPLIER = 0.5;
const MAX_DRAW_MULTIPLIER = 1.5;
const KNOWN_UNKNOWN_WORDS_STORAGE_KEY = "known and unknown words";
const DEBUG_KNOWN_UNKNOWN_WORDS = true;
const STORAGE_ENABLED = process.env.NODE_ENV === "production";
const LEARNING_WORD_THRESHOLD = 2;
const KNOWN_WORD_THRESHOLD = 4;
const ROUND_MIX = {
    known: 0.6,
    learning: 0.3,
    unknown: 0.1,
};
const POLYGLOT_PREFERRED_LANGUAGE_CODES = ["fr", "de", "it", "pt", "ja", "nl", "ru", "tr", "zh"];
const POLYGLOT_LANGUAGE_OPTIONS = ["en", ...WIKDICT_SUPPORTED_LANGUAGE_CODES.filter((languageCode) => languageCode !== "en")];
const MIN_POLYGLOT_LANGUAGE_COUNT = 3;
const MAX_POLYGLOT_LANGUAGE_COUNT = 1 + POLYGLOT_PREFERRED_LANGUAGE_CODES.length;

const isMobileViewport = () =>
    window.matchMedia?.("(max-width: 900px), (pointer: coarse)")?.matches ||
    window.innerWidth <= 900;

const getDeviceWordLimit = () => {
    return ABSOLUTE_MAX_WORDS_TO_GUESS;
};

const TOTAL_ORDERED_WORD_COUNT = getOrderedWordCount();
const TOTAL_PRELOADED_WORD_COUNT = Math.min(TOTAL_ORDERED_WORD_COUNT, PRELOADED_WORD_LIMIT);
const WIKDICT_LANGUAGE_CODE_SET = new Set(WIKDICT_SUPPORTED_LANGUAGE_CODES);

const buildWordMatchDictionary = () => ({});
const getLanguagePairKey = (languages) => [...languages].sort().join("_");
const normalizeEnglishWord = (word) =>
    (word || "").trim().toLowerCase().replace(/^to\s+/, "");
const shuffleArray = (items) => {
    const array = [...items];
    for (let index = array.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [array[index], array[swapIndex]] = [array[swapIndex], array[index]];
    }
    return array;
};

const readKnownAndUnknownWords = () => {
    if (!STORAGE_ENABLED) {
        return {};
    }

    try {
        return JSON.parse(localStorage.getItem(KNOWN_UNKNOWN_WORDS_STORAGE_KEY)) || {};
    } catch (error) {
        console.log(error, ` there is no such key in local storage such as ${KNOWN_UNKNOWN_WORDS_STORAGE_KEY}`);
        return {};
    }
};

const normalizeProgressByPair = (pairData) => {
    if (!pairData) {
        return {};
    }

    if (!Array.isArray(pairData)) {
        return pairData;
    }

    return Object.fromEntries(
        pairData.map((entry) => [
            entry.matchKey,
            {
                ...entry,
                masteryScore: entry.correctCount || 0,
                seenCount: entry.correctCount || 0,
                correctCount: entry.correctCount || 0,
                wrongCount: 0,
                streak: entry.correctCount || 0,
            },
        ])
    );
};

const getWordMasteryBand = (entry) => {
    const masteryScore = entry?.masteryScore || 0;
    if (masteryScore >= KNOWN_WORD_THRESHOLD) {
        return "known";
    }
    if (masteryScore >= LEARNING_WORD_THRESHOLD) {
        return "learning";
    }
    return "unknown";
};

const updateKnownAndUnknownWords = (currentDictionary, languagesPicked, matchedPairs) => {
    if (!matchedPairs?.length) {
        return currentDictionary;
    }

    const pairKey = getLanguagePairKey(languagesPicked);
    const progressByMatchKey = {
        ...normalizeProgressByPair(currentDictionary[pairKey]),
    };

    matchedPairs.forEach((matchedPair) => {
        const existingEntry = progressByMatchKey[matchedPair.matchKey] || {};
        progressByMatchKey[matchedPair.matchKey] = {
            ...(existingEntry || {}),
            ...matchedPair.words,
            matchKey: matchedPair.matchKey,
            masteryScore: Math.min((existingEntry.masteryScore || 0) + 1, KNOWN_WORD_THRESHOLD + 2),
            seenCount: (existingEntry.seenCount || 0) + 1,
            correctCount: (existingEntry.correctCount || 0) + 1,
            wrongCount: existingEntry.wrongCount || 0,
            streak: (existingEntry.streak || 0) + 1,
            lastSeenAt: Date.now(),
            lastCorrectAt: Date.now(),
        };
    });

    return {
        ...currentDictionary,
        [pairKey]: progressByMatchKey,
    };
};

const prioritizeIndicesForLearning = (eligibleIndices, languagesPicked, knownAndUnknownWords) => {
    const safeKnownAndUnknownWords = knownAndUnknownWords || {};
    const pairKey = getLanguagePairKey(languagesPicked);
    const pairData = normalizeProgressByPair(safeKnownAndUnknownWords[pairKey]);

    if (!pairData) {
        return eligibleIndices;
    }

    const unknownMatchKeys = new Set(
        Object.values(pairData)
            .filter((entry) => getWordMasteryBand(entry) === "unknown")
            .map((entry) => entry.matchKey)
    );
    const learningMatchKeys = new Set(
        Object.values(pairData)
            .filter((entry) => getWordMasteryBand(entry) === "learning")
            .map((entry) => entry.matchKey)
    );
    const prioritizedUnknown = eligibleIndices.filter((index) => unknownMatchKeys.has(index));
    const prioritizedLearning = eligibleIndices.filter((index) => learningMatchKeys.has(index) && !unknownMatchKeys.has(index));
    const remainingEligible = eligibleIndices.filter((index) => !unknownMatchKeys.has(index) && !learningMatchKeys.has(index));

    return [...prioritizedUnknown, ...prioritizedLearning, ...remainingEligible];
};

const allocateRoundMixCounts = (totalCount) => {
    const unknownCount = Math.min(totalCount, Math.max(1, Math.round(totalCount * ROUND_MIX.unknown)));
    const learningCount = Math.min(
        totalCount - unknownCount,
        Math.max(1, Math.round(totalCount * ROUND_MIX.learning))
    );
    const knownCount = Math.max(0, totalCount - unknownCount - learningCount);

    return {
        known: knownCount,
        learning: learningCount,
        unknown: unknownCount,
    };
};

const selectByMasteryMix = (candidates, totalCount, progressByMatchKey) => {
    const quotas = allocateRoundMixCounts(totalCount);
    const buckets = {
        known: [],
        learning: [],
        unknown: [],
    };

    candidates.forEach((candidate) => {
        const band = getWordMasteryBand(progressByMatchKey[candidate.matchKey]);
        buckets[band].push(candidate);
    });

    const selected = [];
    Object.entries(quotas).forEach(([band, targetCount]) => {
        selected.push(...shuffleArray(buckets[band]).slice(0, targetCount));
    });

    if (selected.length < totalCount) {
        const selectedMatchKeys = new Set(selected.map((candidate) => candidate.matchKey));
        const remaining = shuffleArray(candidates.filter((candidate) => !selectedMatchKeys.has(candidate.matchKey)));
        selected.push(...remaining.slice(0, totalCount - selected.length));
    }

    return shuffleArray(selected).slice(0, totalCount);
};

const getProgressSummary = (knownAndUnknownWords, languagesPicked) => {
    const progressByMatchKey = normalizeProgressByPair(
        knownAndUnknownWords?.[getLanguagePairKey(languagesPicked)]
    );
    const entries = Object.values(progressByMatchKey);

    return entries.reduce((summary, entry) => {
        const band = getWordMasteryBand(entry);
        summary[band] += 1;
        summary.totalSeen += 1;
        summary.correct += entry.correctCount || 0;
        summary.wrong += entry.wrongCount || 0;
        return summary;
    }, {
        totalSeen: 0,
        known: 0,
        learning: 0,
        unknown: 0,
        correct: 0,
        wrong: 0,
    });
};

export const getSharedMatchKeys = (languageData) => {
    if (!languageData?.length) {
        return [];
    }

    const matchKeySets = languageData.map((language) =>
        new Set((language.words || []).map((word) => word.matchKey))
    );
    const firstColumnMatchKeys = languageData[0].words || [];

    return firstColumnMatchKeys
        .map((word) => word.matchKey)
        .filter((matchKey) => matchKeySets.every((matchKeySet) => matchKeySet.has(matchKey)));
};

export const trimLanguageDataToCount = (languageData, nextWordsToGuess) => {
    const sharedMatchKeys = new Set(getSharedMatchKeys(languageData).slice(0, nextWordsToGuess));

    return languageData.map((language) => ({
        ...language,
        words: (language.words || []).filter((word) => sharedMatchKeys.has(word.matchKey)),
    }));
};

export const mergeCarryoverWordsIntoNewRound = (newLanguageData, carryoverLanguageData, nextWordsToGuess) => {
    const carryoverMatchKeys = new Set(getSharedMatchKeys(carryoverLanguageData));

    if (carryoverMatchKeys.size === 0) {
        return shuffleNonAnchorLanguageData(newLanguageData.map((language) => ({
            ...language,
            words: shuffleArray((language.words || []).slice(0, nextWordsToGuess)),
        })));
    }

    return shuffleNonAnchorLanguageData(newLanguageData.map((language) => {
        const carryoverLanguage = carryoverLanguageData.find((candidate) => candidate.code === language.code);
        const carryoverWords = (carryoverLanguage?.words || [])
            .filter((word) => carryoverMatchKeys.has(word.matchKey));
        const freshWords = (language.words || [])
            .filter((word) => !carryoverMatchKeys.has(word.matchKey));

        return {
            ...language,
            words: shuffleArray([...carryoverWords, ...freshWords].slice(0, nextWordsToGuess)),
        };
    }));
};

export const shouldStartNewRound = (matchedCount, remainingPairCount) =>
    matchedCount > 0 && remainingPairCount <= 1;

const languageDataHasAlignedMatches = (languageData) => {
    if (languageData.length < 2) {
        return false;
    }

    const rowCount = Math.min(...languageData.map((language) => (language.words || []).length));
    for (let index = 0; index < rowCount; index += 1) {
        const rowMatchKeys = languageData.map((language) => language.words[index]?.matchKey);
        const firstMatchKey = rowMatchKeys[0];
        if (firstMatchKey !== undefined && rowMatchKeys.every((matchKey) => matchKey === firstMatchKey)) {
            return true;
        }
    }

    return false;
};

const unalignLanguageData = (languageData) => {
    const nextLanguageData = languageData.map((language) => ({
        ...language,
        words: [...(language.words || [])],
    }));
    const rowCount = Math.min(...nextLanguageData.map((language) => language.words.length));

    for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        const rowMatchKeys = nextLanguageData.map((language) => language.words[rowIndex]?.matchKey);
        const firstMatchKey = rowMatchKeys[0];
        const rowIsAligned = firstMatchKey !== undefined && rowMatchKeys.every((matchKey) => matchKey === firstMatchKey);

        if (!rowIsAligned) {
            continue;
        }

        for (let columnIndex = 1; columnIndex < nextLanguageData.length; columnIndex += 1) {
            const swapIndex = nextLanguageData[columnIndex].words.findIndex((word, candidateIndex) =>
                candidateIndex !== rowIndex &&
                word?.matchKey !== firstMatchKey &&
                nextLanguageData[0].words[candidateIndex]?.matchKey !== word?.matchKey
            );

            if (swapIndex !== -1) {
                [nextLanguageData[columnIndex].words[rowIndex], nextLanguageData[columnIndex].words[swapIndex]] = [
                    nextLanguageData[columnIndex].words[swapIndex],
                    nextLanguageData[columnIndex].words[rowIndex],
                ];
                break;
            }
        }
    }

    return nextLanguageData;
};

const shuffleNonAnchorLanguageData = (languageData) => {
    for (let attempt = 0; attempt < 20; attempt += 1) {
        const candidateLanguageData = languageData.map((language, index) => ({
            ...language,
            words: index === 0 ? language.words || [] : shuffleArray(language.words || []),
        }));

        if (!languageDataHasAlignedMatches(candidateLanguageData)) {
            return candidateLanguageData;
        }
    }

    return unalignLanguageData(languageData);
};

const getWordsToGuessForMultiplier = (drawPoolMultiplier, deviceWordLimit, drawCount = TOTAL_ORDERED_WORD_COUNT) =>
    Math.max(
        MIN_WORDS_TO_GUESS,
        Math.min(
            Math.round(DEFAULT_WORDS_TO_GUESS * drawPoolMultiplier),
            drawCount,
            deviceWordLimit,
            ABSOLUTE_MAX_WORDS_TO_GUESS
        )
    );

export const getWikDictTargetLanguageCode = (languages) => {
    if (languages?.length !== 2 || !languages.includes("en")) {
        return null;
    }

    const targetLanguageCode = languages.find((languageCode) => languageCode !== "en");
    return WIKDICT_LANGUAGE_CODE_SET.has(targetLanguageCode) ? targetLanguageCode : null;
};

const getWikDictPairsForLanguages = (languages) => {
    const targetLanguageCode = getWikDictTargetLanguageCode(languages);
    return targetLanguageCode ? WIKDICT_ENGLISH_PAIRS_BY_LANGUAGE[targetLanguageCode] || [] : [];
};

const getWikDictChunkMetadata = (languageCode) =>
    WIKDICT_CHUNK_METADATA_BY_LANGUAGE[languageCode] || null;

const WIKDICT_TARGET_SCRIPT_MATCHERS = {
    ja: /[\u3040-\u30ff\u3400-\u9fff]/,
    ru: /[\u0400-\u04ff]/,
    zh: /[\u3400-\u9fff]/,
};

const BLOCKED_WORD_PATTERNS = [
    /\b(?:pre)?cum\b/i,
    /\b(?:dip|bull|bat)?shit(?:head|hole|load|ton|ter|ty|s|eater|-for-brains|-stirrer)?\b/i,
    /\bfuck(?:er|ing|ed|able|ability|face|tard|up)?\b/i,
    /\bmotherfuck/i,
    /\basshole\b/i,
    /\bcunt\b/i,
    /\bbitch\b/i,
    /\bwanker\b/i,
    /\bsemen\b/i,
    /\bpiss(?:ed|ing)?\b/i,
    /\bslut\b/i,
    /\bwhore\b/i,
];

const containsBlockedWord = (value) =>
    BLOCKED_WORD_PATTERNS.some((pattern) => pattern.test(String(value || "")));

const normalizeWikDictTranslation = (translation) =>
    String(translation || "")
        .replace(/\[\[([^\]|]+)\|([^\]]+)\]\]/g, "$2")
        .replace(/\[\[([^\]]+)\]\]/g, "$1")
        .replace(/\s+\((?:adj|noun|verb|adverb|pronoun)\)$/i, "")
        .trim();

const isAllowedTargetScriptFallback = (translation) =>
    /^[A-Z0-9Ａ-Ｚ０-９㎆ＣＤＰＵＮ.-]+$/u.test(translation);

const isUsableWikDictPair = ([english, translation], languageCode) => {
    const normalizedEnglish = String(english || "").trim();
    const normalizedTranslation = normalizeWikDictTranslation(translation);
    const targetScriptMatcher = WIKDICT_TARGET_SCRIPT_MATCHERS[languageCode];

    if (!normalizedEnglish || !normalizedTranslation) {
        return false;
    }

    if (containsBlockedWord(normalizedEnglish) || containsBlockedWord(normalizedTranslation)) {
        return false;
    }

    if (
        /^suffix/i.test(normalizedTranslation) ||
        /^preposition/i.test(normalizedTranslation) ||
        normalizedTranslation === "[[#" ||
        normalizedTranslation === "}}" ||
        /<[^>]+>|&[a-z]+;/i.test(normalizedTranslation)
    ) {
        return false;
    }

    if (targetScriptMatcher && !targetScriptMatcher.test(normalizedTranslation) && !isAllowedTargetScriptFallback(normalizedTranslation)) {
        return false;
    }

    return true;
};

const cleanWikDictPairs = (pairs, languageCode) =>
    pairs
        .map(([english, translation]) => [english, normalizeWikDictTranslation(translation)])
        .filter((pair) => isUsableWikDictPair(pair, languageCode));

const getLoadedWikDictExtraPairs = (languageCode, wikDictChunkStateByLanguage) =>
    wikDictChunkStateByLanguage[languageCode]?.extraPairs || [];

const getLoadedWikDictPairsForLanguages = (languages, wikDictChunkStateByLanguage) => {
    const targetLanguageCode = getWikDictTargetLanguageCode(languages);
    if (!targetLanguageCode) {
        return [];
    }

    return cleanWikDictPairs([
        ...(WIKDICT_ENGLISH_PAIRS_BY_LANGUAGE[targetLanguageCode] || []),
        ...getLoadedWikDictExtraPairs(targetLanguageCode, wikDictChunkStateByLanguage),
    ], targetLanguageCode);
};

const getPolyglotLanguages = (languages, languageCount = 4) => {
    const targetLanguageCount = Math.max(
        MIN_POLYGLOT_LANGUAGE_COUNT,
        Math.min(languageCount, MAX_POLYGLOT_LANGUAGE_COUNT)
    );
    const languagesWithEnglish = languages.includes("en") ? languages : ["en", ...languages];
    const dedupedLanguages = [...new Set(languagesWithEnglish)]
        .filter((languageCode) => languageCode === "en" || WIKDICT_LANGUAGE_CODE_SET.has(languageCode));

    POLYGLOT_PREFERRED_LANGUAGE_CODES.forEach((languageCode) => {
        if (dedupedLanguages.length >= targetLanguageCount) {
            return;
        }
        if (!dedupedLanguages.includes(languageCode)) {
            dedupedLanguages.push(languageCode);
        }
    });

    return dedupedLanguages.slice(0, targetLanguageCount);
};

const buildPolyglotPairPool = (languages, wikDictChunkStateByLanguage) => {
    const targetLanguageCodes = languages.filter((languageCode) => languageCode !== "en");
    const translationsByEnglishKey = {};

    targetLanguageCodes.forEach((languageCode) => {
        getLoadedWikDictPairsForLanguages(["en", languageCode], wikDictChunkStateByLanguage)
            .forEach(([english, translation]) => {
                const englishKey = normalizeEnglishWord(english);
                if (!englishKey || translationsByEnglishKey[englishKey]?.[languageCode]) {
                    return;
                }

                translationsByEnglishKey[englishKey] = {
                    ...(translationsByEnglishKey[englishKey] || {}),
                    english,
                    [languageCode]: translation,
                };
            });
    });

    return Object.entries(translationsByEnglishKey)
        .filter(([, translations]) => targetLanguageCodes.every((languageCode) => translations[languageCode]))
        .map(([englishKey, translations]) => ({
            englishKey,
            english: translations.english,
            translations,
            matchKey: englishKey,
        }));
};

export const getPreloadedWordPoolCount = (languages) => {
    const targetLanguageCode = getWikDictTargetLanguageCode(languages);
    if (!targetLanguageCode) {
        return TOTAL_PRELOADED_WORD_COUNT;
    }

    const metadata = getWikDictChunkMetadata(targetLanguageCode);
    return metadata?.preloadedPairCount ?? Math.min(getWikDictPairsForLanguages(languages).length, PRELOADED_WORD_LIMIT);
};

const getAvailableWordPoolCount = (languages) => {
    const targetLanguageCode = getWikDictTargetLanguageCode(languages);
    if (!targetLanguageCode) {
        return TOTAL_PRELOADED_WORD_COUNT;
    }

    const metadata = getWikDictChunkMetadata(targetLanguageCode);
    return metadata?.totalPairCount ?? getPreloadedWordPoolCount(languages);
};

const getLoadedWordPoolCount = (languages, wikDictChunkStateByLanguage) => {
    const targetLanguageCode = getWikDictTargetLanguageCode(languages);
    if (!targetLanguageCode) {
        return TOTAL_PRELOADED_WORD_COUNT;
    }

    return getLoadedWikDictPairsForLanguages(languages, wikDictChunkStateByLanguage).length;
};

const ColumnsWrapper = () => {
    const local_data_storage_key = "language matchup data"
    let data_retrieved_from_local_storage = "" 
    if (STORAGE_ENABLED) {
        try{data_retrieved_from_local_storage = JSON.parse(localStorage.getItem(local_data_storage_key))}
        catch (error){console.log(error,` there is no such key in local storage such as ${local_data_storage_key}`)}
    }


    const maxWordsToGuess = Math.min(getDeviceWordLimit(), ABSOLUTE_MAX_WORDS_TO_GUESS);
    const initialKnownAndUnknownWords = readKnownAndUnknownWords();
    const initialWordPoolCount = getPreloadedWordPoolCount(["en", "es"]);
    const initialDrawCount = Math.min(DEFAULT_WORDS_TO_DRAW_FROM, initialWordPoolCount);
    const initial_state_data = {
        languages_picked: ["en", "es"],
        numberOfWordsToGuess: getWordsToGuessForMultiplier(INITIAL_DRAW_MULTIPLIER, maxWordsToGuess, initialDrawCount),
        numberOfWordsToDrawFrom: initialDrawCount,
        minimumNumberOfWords: 0,
        drawPoolMultiplier: INITIAL_DRAW_MULTIPLIER,
        timeToGuess: 20000, // 20 seconds
        timeOfSessionStart: Date.now(),
        languageData: [],
        knownAndUnknownWords: initialKnownAndUnknownWords,
        wordMatchDictionary: {},
    }

    data_retrieved_from_local_storage = initial_state_data //data_retrieved_from_local_storage === null ? initial_state_data : data_retrieved_from_local_storage
    const [state, setState] = useState({
        ...initial_state_data,
        ...(data_retrieved_from_local_storage || {}),
        drawPoolMultiplier: data_retrieved_from_local_storage?.drawPoolMultiplier || INITIAL_DRAW_MULTIPLIER,
        knownAndUnknownWords: data_retrieved_from_local_storage?.knownAndUnknownWords || initialKnownAndUnknownWords,
    });
    const [hintsEnabled, setHintsEnabled] = useState(false);
    const [soundsEnabled, setSoundsEnabled] = useState(true);
    const [polyglotModeEnabled, setPolyglotModeEnabled] = useState(false);
    const [polyglotSelectedLanguageCodes, setPolyglotSelectedLanguageCodes] = useState(() =>
        getPolyglotLanguages(initial_state_data.languages_picked, 4)
    );
    const [menuOpen, setMenuOpen] = useState(false);
    const [deviceWordLimit, setDeviceWordLimit] = useState(maxWordsToGuess);
    const [allCategoriesMode, setAllCategoriesMode] = useState(true);
    const [wikDictChunkStateByLanguage, setWikDictChunkStateByLanguage] = useState({});
    const wikDictChunkRequestsInFlight = useRef(new Set());
    const [selectedCategories, setSelectedCategories] = useState(
        Object.fromEntries(Object.keys(WORD_CATEGORIES).map((category) => [category, false]))
    );
    const [currentRoundMix, setCurrentRoundMix] = useState({
        known: 0,
        learning: 0,
        unknown: 0,
    });
    const categoryGroups = WORD_CATEGORY_TREE.map((category) => ({
        ...category,
        children: (category.children || []).filter((child) => WORD_CATEGORIES[child.id]),
    })).filter((category) => category.children.length > 0);
    const getActivePolyglotLanguages = () =>
        getPolyglotLanguages(polyglotSelectedLanguageCodes, polyglotSelectedLanguageCodes.length);
    console.log(data_retrieved_from_local_storage)



    // Utility functions
    const fullShuffle = (arr) => {
        let array = [...arr]
        for (let i = array.length - 1; i > 0; i--) {
            let j = Math.floor(Math.random() * (i + 1));
            while (i === j) {
                j = Math.floor(Math.random() * (i + 1));
            }
            [array[i], array[j]] = [array[j], array[i]];

        }
        return array;
    };

    const shuffle = (arr) => {
        let array = [...arr]
        for (let i = array.length - 1; i > 0; i--) {
            let j = Math.floor(Math.random() * (i + 1));

            [array[i], array[j]] = [array[j], array[i]];

        }
        return array;
    };

    const generateNewLanguageData = (languages_picked, drawCount = state.numberOfWordsToDrawFrom) => {
        const pairKey = getLanguagePairKey(languages_picked);
        const progressByMatchKey = normalizeProgressByPair(state.knownAndUnknownWords?.[pairKey]);
        const wikDictTargetLanguageCode = getWikDictTargetLanguageCode(languages_picked);

        if (polyglotModeEnabled && languages_picked.includes("en") && languages_picked.length >= 3) {
            const cappedWordsToGuess = Math.min(state.numberOfWordsToGuess, deviceWordLimit, ABSOLUTE_MAX_WORDS_TO_GUESS);
            const enabledCategories = Object.entries(selectedCategories)
                .filter(([, isEnabled]) => isEnabled)
                .map(([category]) => category);
            const shouldFilterByCategory = !allCategoriesMode && enabledCategories.length > 0;
            const allPolyglotPairs = buildPolyglotPairPool(languages_picked, wikDictChunkStateByLanguage);
            const eligiblePairs = shouldFilterByCategory
                ? allPolyglotPairs.filter((pair) =>
                    enabledCategories.some((category) => WORD_CATEGORIES[category]?.has(pair.englishKey))
                )
                : allPolyglotPairs;
            const candidatePoolPairs = eligiblePairs.slice(0, Math.max(drawCount, cappedWordsToGuess));
            const selectedPairs = selectByMasteryMix(candidatePoolPairs, cappedWordsToGuess, progressByMatchKey);
            const nextRoundMix = selectedPairs.reduce((counts, pair) => {
                counts[getWordMasteryBand(progressByMatchKey[pair.matchKey])] += 1;
                return counts;
            }, { known: 0, learning: 0, unknown: 0 });
            setCurrentRoundMix(nextRoundMix);

            return shuffleNonAnchorLanguageData(languages_picked.map((languageCode, index) => {
                const words = selectedPairs.map((pair) => ({
                    text: languageCode === "en" ? pair.english : pair.translations[languageCode],
                    matchKey: pair.matchKey,
                }));

                return {
                    code: languageCode,
                    title: getLanguageName(languageCode),
                    words: index === 0 ? words : fullShuffle(words),
                };
            }));
        }

        if (wikDictTargetLanguageCode) {
            const cappedWordsToGuess = Math.min(state.numberOfWordsToGuess, deviceWordLimit, ABSOLUTE_MAX_WORDS_TO_GUESS);
            const enabledCategories = Object.entries(selectedCategories)
                .filter(([, isEnabled]) => isEnabled)
                .map(([category]) => category);
            const shouldFilterByCategory = !allCategoriesMode && enabledCategories.length > 0;
            const loadedDrawCount = Math.min(drawCount, getLoadedWordPoolCount(languages_picked, wikDictChunkStateByLanguage));
            const allWikDictPairs = getLoadedWikDictPairsForLanguages(languages_picked, wikDictChunkStateByLanguage).map(([english, translation], index) => ({
                english,
                translation,
                matchKey: index,
            }));
            const eligiblePairs = shouldFilterByCategory
                ? allWikDictPairs.filter((pair) => {
                    const englishWord = normalizeEnglishWord(pair.english);
                    return enabledCategories.some((category) => WORD_CATEGORIES[category]?.has(englishWord));
                })
                : allWikDictPairs;
            const eligibleMatchKeys = eligiblePairs.map((pair) => pair.matchKey);
            const orderedMatchKeys = eligibleMatchKeys.length > 0
                ? prioritizeIndicesForLearning(eligibleMatchKeys, languages_picked, state.knownAndUnknownWords)
                : eligibleMatchKeys;
            const pairByMatchKey = new Map(eligiblePairs.map((pair) => [pair.matchKey, pair]));
            const orderedPairs = orderedMatchKeys
                .map((matchKey) => pairByMatchKey.get(matchKey))
                .filter((pair) => pair !== undefined);
            const candidatePoolPairs = orderedPairs.slice(0, Math.max(loadedDrawCount, cappedWordsToGuess));
            const selectedPairs = selectByMasteryMix(candidatePoolPairs, cappedWordsToGuess, progressByMatchKey);
            const nextRoundMix = selectedPairs.reduce((counts, pair) => {
                counts[getWordMasteryBand(progressByMatchKey[pair.matchKey])] += 1;
                return counts;
            }, { known: 0, learning: 0, unknown: 0 });
            setCurrentRoundMix(nextRoundMix);
            const wordsByLanguage = {
                en: selectedPairs.map((pair) => ({ text: pair.english, matchKey: pair.matchKey })),
                [wikDictTargetLanguageCode]: selectedPairs.map((pair) => ({ text: pair.translation, matchKey: pair.matchKey })),
            };

            console.log("generated round", {
                drawCount: loadedDrawCount,
                candidatePoolSize: candidatePoolPairs.length,
                numberOfWordsToGuess: cappedWordsToGuess,
                selectedMatchKeys: selectedPairs.map((pair) => pair.matchKey),
                source: `wikdict-${wikDictTargetLanguageCode}`,
            });

            return shuffleNonAnchorLanguageData(languages_picked.map((languageCode, index) => ({
                code: languageCode,
                title: getLanguageName(languageCode),
                words: index === 0 ? wordsByLanguage[languageCode] : fullShuffle(wordsByLanguage[languageCode]),
            })));
        }

        const cappedWordsToGuess = Math.min(state.numberOfWordsToGuess, deviceWordLimit, ABSOLUTE_MAX_WORDS_TO_GUESS);
        const cappedDrawCount = Math.min(drawCount, getPreloadedWordPoolCount(languages_picked));
        const drawPoolIndices = [...Array(cappedDrawCount).keys()];
        const enabledCategories = Object.entries(selectedCategories)
            .filter(([, isEnabled]) => isEnabled)
            .map(([category]) => category);
        const shouldFilterByCategory = !allCategoriesMode && enabledCategories.length > 0;
        const eligibleIndices = shouldFilterByCategory
            ? Array.from({ length: getPreloadedWordPoolCount(languages_picked) }, (_, index) => index).filter((index) => {
                const englishWord = normalizeEnglishWord(getWordAtOrderedIndex("en", index));
                return enabledCategories.some((category) => WORD_CATEGORIES[category]?.has(englishWord));
            })
            : drawPoolIndices;
        const isSafeLegacyIndex = (index) =>
            languages_picked.every((languageCode) => !containsBlockedWord(getWordAtOrderedIndex(languageCode, index)));
        const safeEligibleIndices = eligibleIndices.filter(isSafeLegacyIndex);
        const safeDrawPoolIndices = drawPoolIndices.filter(isSafeLegacyIndex);
        const orderedIndices = safeEligibleIndices.length > 0
            ? prioritizeIndicesForLearning(safeEligibleIndices, languages_picked, state.knownAndUnknownWords)
            : prioritizeIndicesForLearning(safeDrawPoolIndices, languages_picked, state.knownAndUnknownWords);
        const candidatePoolIndices = orderedIndices.slice(0, Math.max(drawCount, cappedWordsToGuess));
        const candidatePoolEntries = candidatePoolIndices.map((matchKey) => ({ matchKey }));
        const selectedEntries = selectByMasteryMix(candidatePoolEntries, cappedWordsToGuess, progressByMatchKey);
        const indices_of_words_to_draw_from = selectedEntries.map((entry) => entry.matchKey);
        const nextRoundMix = selectedEntries.reduce((counts, entry) => {
            counts[getWordMasteryBand(progressByMatchKey[entry.matchKey])] += 1;
            return counts;
        }, { known: 0, learning: 0, unknown: 0 });
        setCurrentRoundMix(nextRoundMix);
        const language_one_words = getWordsAtOrderedIndices(languages_picked[0], indices_of_words_to_draw_from)
            .map((text, index) => ({
                text,
                matchKey: indices_of_words_to_draw_from[index],
            }));
        const language_two_words = getWordsAtOrderedIndices(languages_picked[1], indices_of_words_to_draw_from)
            .map((text, index) => ({
                text,
                matchKey: indices_of_words_to_draw_from[index],
            }));

        console.log("generated round", {
            drawCount,
            candidatePoolSize: candidatePoolIndices.length,
            numberOfWordsToGuess: cappedWordsToGuess,
            selectedMatchKeys: indices_of_words_to_draw_from,
        });

        const newLanguageData = [{
            code: languages_picked[0],
            title: getLanguageName(languages_picked[0]),
            words: language_one_words

        }, {
            code: languages_picked[1],
            title: getLanguageName(languages_picked[1]),
            words: fullShuffle(language_two_words)

        }, ];
        return shuffleNonAnchorLanguageData(newLanguageData);
    };

    // Generate the initial round and regenerate only when languages change.
    useEffect(() => {
        const activeLanguagesPicked = polyglotModeEnabled ? getActivePolyglotLanguages() : state.languages_picked;
        const newLanguageData = generateNewLanguageData(activeLanguagesPicked);
        const newWordMatchDictionary = buildWordMatchDictionary(newLanguageData);

        setState((prev) => ({
            ...prev,
            languageData: newLanguageData,
            wordMatchDictionary: newWordMatchDictionary,
        }));
    }, [state.languages_picked, selectedCategories, allCategoriesMode, wikDictChunkStateByLanguage, polyglotModeEnabled, polyglotSelectedLanguageCodes]);

    useEffect(() => {
        const targetLanguageCode = getWikDictTargetLanguageCode(state.languages_picked);
        if (!targetLanguageCode) {
            return undefined;
        }

        const metadata = getWikDictChunkMetadata(targetLanguageCode);
        if (!metadata || metadata.extraChunkCount === 0) {
            return undefined;
        }

        const desiredPairCount = Math.min(
            getAvailableWordPoolCount(state.languages_picked),
            Math.max(state.numberOfWordsToDrawFrom, state.numberOfWordsToGuess)
        );
        const loadedPairCount = getLoadedWordPoolCount(state.languages_picked, wikDictChunkStateByLanguage);

        if (loadedPairCount >= desiredPairCount || desiredPairCount <= metadata.preloadedPairCount) {
            return undefined;
        }

        const neededExtraPairCount = desiredPairCount - metadata.preloadedPairCount;
        const requiredExtraChunkCount = Math.min(
            metadata.extraChunkCount,
            Math.ceil(neededExtraPairCount / WIKDICT_EXTRA_CHUNK_SIZE)
        );
        const loadedChunkPaths = wikDictChunkStateByLanguage[targetLanguageCode]?.loadedChunkPaths || [];
        const missingChunkPaths = metadata.extraChunkPaths
            .slice(0, requiredExtraChunkCount)
            .filter((chunkPath) => !loadedChunkPaths.includes(chunkPath) && !wikDictChunkRequestsInFlight.current.has(chunkPath));

        if (missingChunkPaths.length === 0) {
            return undefined;
        }

        let cancelled = false;
        missingChunkPaths.forEach((chunkPath) => wikDictChunkRequestsInFlight.current.add(chunkPath));

        Promise.all(
            missingChunkPaths.map(async (chunkPath) => ({
                chunkPath,
                chunkPairs: await loadCachedWikDictChunk(chunkPath),
            }))
        )
            .then((loadedChunks) => {
                if (cancelled) {
                    return;
                }

                setWikDictChunkStateByLanguage((prev) => {
                    const previousLanguageState = prev[targetLanguageCode] || { extraPairs: [], loadedChunkPaths: [] };
                    const nextLoadedChunkPaths = [...previousLanguageState.loadedChunkPaths];
                    const nextExtraPairs = [...previousLanguageState.extraPairs];

                    loadedChunks.forEach(({ chunkPath, chunkPairs }) => {
                        if (nextLoadedChunkPaths.includes(chunkPath)) {
                            return;
                        }
                        nextLoadedChunkPaths.push(chunkPath);
                        nextExtraPairs.push(...chunkPairs);
                    });

                    return {
                        ...prev,
                        [targetLanguageCode]: {
                            extraPairs: nextExtraPairs,
                            loadedChunkPaths: nextLoadedChunkPaths,
                        },
                    };
                });
            })
            .catch((error) => {
                console.error("Failed to load WikDict chunk", error);
            })
            .finally(() => {
                missingChunkPaths.forEach((chunkPath) => wikDictChunkRequestsInFlight.current.delete(chunkPath));
            });

        return () => {
            cancelled = true;
        };
    }, [state.languages_picked, state.numberOfWordsToDrawFrom, state.numberOfWordsToGuess, wikDictChunkStateByLanguage]);

    useEffect(() => {
        const handleResize = () => {
            const nextDeviceLimit = Math.min(getDeviceWordLimit(), ABSOLUTE_MAX_WORDS_TO_GUESS);
            setDeviceWordLimit(nextDeviceLimit);
            setState((prev) => ({
                ...prev,
                numberOfWordsToGuess: getWordsToGuessForMultiplier(
                    prev.drawPoolMultiplier,
                    nextDeviceLimit,
                    prev.numberOfWordsToDrawFrom
                ),
            }));
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const requestPolyglotLandscape = async () => {
        if (!isMobileViewport()) {
            return;
        }

        try {
            if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
                await document.documentElement.requestFullscreen();
            }

            if (window.screen?.orientation?.lock) {
                await window.screen.orientation.lock("landscape");
            }
        } catch (error) {
            console.info("Landscape orientation lock was not available.", error);
        }
    };

    useEffect(() => {
        document.body.classList.toggle("polyglot-landscape-mode", polyglotModeEnabled);

        if (!polyglotModeEnabled || !isMobileViewport()) {
            return () => {
                document.body.classList.remove("polyglot-landscape-mode");
            };
        }

        let cancelled = false;

        const lockLandscape = async () => {
            if (!cancelled) {
                await requestPolyglotLandscape();
            }
        };

        lockLandscape();

        return () => {
            cancelled = true;
            document.body.classList.remove("polyglot-landscape-mode");

            try {
                window.screen?.orientation?.unlock?.();
            } catch (error) {
                console.info("Landscape orientation unlock was not available.", error);
            }
        };
    }, [polyglotModeEnabled]);

    const handleCategoryToggle = (category) => {
        setSelectedCategories((prev) => {
            const next = {
                ...prev,
                [category]: !prev[category],
            };
            const hasSpecificCategory = Object.values(next).some(Boolean);
            setAllCategoriesMode(!hasSpecificCategory);
            return next;
        });
    };

    const isCategoryGroupSelected = (category) =>
        !allCategoriesMode && category.children.every((child) => selectedCategories[child.id]);

    const isCategoryGroupPartlySelected = (category) =>
        !allCategoriesMode &&
        category.children.some((child) => selectedCategories[child.id]) &&
        !isCategoryGroupSelected(category);

    const handleCategoryGroupToggle = (category) => {
        const categoryIsSelected = isCategoryGroupSelected(category);
        const next = Object.fromEntries(
            Object.keys(WORD_CATEGORIES).map((categoryId) => [
                categoryId,
                categoryIsSelected
                    ? false
                    : category.children.some((child) => child.id === categoryId),
            ])
        );
        const hasSpecificCategory = Object.values(next).some(Boolean);

        setAllCategoriesMode(!hasSpecificCategory);
        setSelectedCategories(next);
    };

    const handleAllCategoriesToggle = () => {
        setAllCategoriesMode(true);
        setSelectedCategories(
            Object.fromEntries(Object.keys(WORD_CATEGORIES).map((category) => [category, false]))
        );
    };

    const handlePolyglotLanguageToggle = (languageCode) => {
        if (languageCode === "en") {
            return;
        }

        setPolyglotSelectedLanguageCodes((currentLanguageCodes) => {
            const normalizedLanguageCodes = getPolyglotLanguages(currentLanguageCodes, currentLanguageCodes.length);
            const isSelected = normalizedLanguageCodes.includes(languageCode);

            if (isSelected && normalizedLanguageCodes.length <= MIN_POLYGLOT_LANGUAGE_COUNT) {
                return normalizedLanguageCodes;
            }

            if (isSelected) {
                return normalizedLanguageCodes.filter((currentLanguageCode) => currentLanguageCode !== languageCode);
            }

            return [...normalizedLanguageCodes, languageCode];
        });
    };

    // Function to handle game state updates
    const getGameState = (data) => {


        if (Object.keys(data).includes("action")) {
            if (data["action"] === "change_language") {
                let newly_picked_languages = polyglotModeEnabled
                    ? getActivePolyglotLanguages()
                    : state.languages_picked

                if (state.languages_picked.includes(data["changing language to"])) {
                    newly_picked_languages = [newly_picked_languages[1], newly_picked_languages[0], ...newly_picked_languages.slice(2)]
                } else {
                    newly_picked_languages[data["column number"]] = data["changing language to"]
                }

                setState((prev) => ({
                    ...prev,
                    languages_picked: Array.from(newly_picked_languages) //newly_picked_languages // Example of language switch
                }));
            }

        } else {
            const payload = Array.isArray(data)
                ? { languageData: data, matchedCount: 0, multiplierDelta: 0 }
                : data;
            const updatedLanguageData = payload.languageData;
            const matchedPairs = payload.matchedPairs || [];
            const multiplierDelta = payload.multiplierDelta || 0;
            const nextKnownAndUnknownWords = updateKnownAndUnknownWords(
                state.knownAndUnknownWords || {},
                polyglotModeEnabled ? getActivePolyglotLanguages() : state.languages_picked,
                matchedPairs
            );

            if (DEBUG_KNOWN_UNKNOWN_WORDS && matchedPairs.length > 0) {
                console.log("known and unknown words", nextKnownAndUnknownWords);
            }
            const nextDrawPoolMultiplier = Math.max(
                MIN_DRAW_MULTIPLIER,
                Math.min(state.drawPoolMultiplier + multiplierDelta, MAX_DRAW_MULTIPLIER)
            );
            const activeLanguagesPicked = polyglotModeEnabled ? getActivePolyglotLanguages() : state.languages_picked;
            const activeWordPoolCount = polyglotModeEnabled
                ? Math.max(buildPolyglotPairPool(activeLanguagesPicked, wikDictChunkStateByLanguage).length, MIN_WORDS_TO_DRAW_FROM + 1)
                : getAvailableWordPoolCount(state.languages_picked);
            const nextDrawCount = Math.max(
                MIN_WORDS_TO_DRAW_FROM,
                Math.min(
                    Math.round(state.numberOfWordsToDrawFrom * nextDrawPoolMultiplier),
                    activeWordPoolCount - 1
                )
            );
            const nextWordsToGuess = getWordsToGuessForMultiplier(
                nextDrawPoolMultiplier,
                deviceWordLimit,
                nextDrawCount
            );

            console.log("board sizing", {
                drawPoolMultiplier: nextDrawPoolMultiplier,
                numberOfWordsToDrawFrom: nextDrawCount,
                numberOfWordsToGuess: nextWordsToGuess,
            });

            const nextLanguageData = trimLanguageDataToCount(updatedLanguageData, nextWordsToGuess);

            const sharedMatchKeysAfterUpdate = getSharedMatchKeys(nextLanguageData);
            const shouldCarryOnePairIntoNewRound = matchedPairs.length > 0 && sharedMatchKeysAfterUpdate.length === 1;

            // Keep the current board until the player has worked it down to one
            // remaining pair. At that point, refill it while carrying that pair
            // forward so the board never becomes empty between rounds.
            // A zero-pair result is retained as an edge-case fallback for a
            // multi-match move that clears the board in one action.
            const startNewRound = shouldStartNewRound(
                matchedPairs.length,
                sharedMatchKeysAfterUpdate.length
            );

            if (startNewRound) {

                const generatedLanguageData = generateNewLanguageData(
                    activeLanguagesPicked,
                    nextDrawCount
                );
                const newLanguageData = shouldCarryOnePairIntoNewRound
                    ? mergeCarryoverWordsIntoNewRound(generatedLanguageData, nextLanguageData, nextWordsToGuess)
                    : generatedLanguageData;

                setState((prev) => ({
                    ...prev,
                    languageData: newLanguageData,
                    drawPoolMultiplier: nextDrawPoolMultiplier,
                    knownAndUnknownWords: nextKnownAndUnknownWords,
                    wordMatchDictionary: buildWordMatchDictionary(newLanguageData),
                    numberOfWordsToDrawFrom: nextDrawCount,
                    numberOfWordsToGuess: nextWordsToGuess,
                }));
            } else {
                setState((prev) => ({
                    ...prev,
                    languageData: nextLanguageData,
                    drawPoolMultiplier: nextDrawPoolMultiplier,
                    knownAndUnknownWords: nextKnownAndUnknownWords,
                    wordMatchDictionary: buildWordMatchDictionary(nextLanguageData),
                    numberOfWordsToDrawFrom: nextDrawCount,
                    numberOfWordsToGuess: nextWordsToGuess,
                }));
            }
        }
    };


    // Function to calculate time elapsed
    const timeElapsed = () => {
        const elapsed = Date.now() - state.timeOfSessionStart;
        setState((prev) => ({
            ...prev,
            timeOfSessionStart: Date.now(),
        }));
        return elapsed;
    };



    let decideNumberOfWordsToDrawFrom = () => {
        const elapsed = timeElapsed();
        const activeWordPoolCount = getAvailableWordPoolCount(state.languages_picked);
        let newCount = Math.ceil(
            state.numberOfWordsToDrawFrom * (1 + 0.05 * (elapsed < state.timeToGuess))

        );
            newCount = newCount < activeWordPoolCount ? newCount: activeWordPoolCount - 1;

        setState((prev) => ({
            ...prev,
            numberOfWordsToDrawFrom: newCount,
        }));
        //console.log(`Number of words to draw from increased to: ${newCount}`);
        // this is the minimum of newCount and the total number of words you can draw from
        // you don't want to be drawing from words that aren't there
        return newCount
    };

    if (STORAGE_ENABLED) {
        localStorage.setItem(local_data_storage_key, JSON.stringify(state))
        localStorage.setItem(KNOWN_UNKNOWN_WORDS_STORAGE_KEY, JSON.stringify(state.knownAndUnknownWords))
    }

    const activeProgressLanguages = polyglotModeEnabled
        ? getActivePolyglotLanguages()
        : state.languages_picked;
    const progressSummary = getProgressSummary(state.knownAndUnknownWords, activeProgressLanguages);
    const totalAttempts = progressSummary.correct + progressSummary.wrong;
    const accuracy = totalAttempts > 0
        ? Math.round((progressSummary.correct / totalAttempts) * 100)
        : 0;
    const activePolyglotLanguageSet = new Set(getActivePolyglotLanguages());

return (
    <div className="language-matchup-app" style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        boxSizing: "border-box",
        padding: "10px",
        gap: "12px",
    }}>
        <h1
            style={{
                margin: "0",
                color: "#ffffff",
                fontSize: "32px",
                fontWeight: 800,
                textAlign: "center",
            }}
        >
            Language Matchup
        </h1>
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                justifyContent: "center",
            }}
        >
            <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "42px",
                    height: "42px",
                    borderRadius: "10px",
                    border: "1px solid rgba(255,255,255,0.35)",
                    background: "rgba(0,0,0,0.22)",
                    color: "#ffffff",
                    fontSize: "22px",
                    cursor: "pointer",
                }}
                aria-label="Toggle word categories menu"
            >
                ☰
            </button>
            <label
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#ffffff",
                    fontWeight: "bold",
                    fontSize: "16px",
                }}
            >
                <input
                    type="checkbox"
                    checked={hintsEnabled}
                    onChange={(event) => setHintsEnabled(event.target.checked)}
                />
                Hint
            </label>
            <label
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#ffffff",
                    fontWeight: "bold",
                    fontSize: "16px",
                }}
            >
                <input
                    type="checkbox"
                    checked={soundsEnabled}
                    onChange={(event) => setSoundsEnabled(event.target.checked)}
                />
                Sounds
            </label>
            <label
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#ffffff",
                    fontWeight: "bold",
                    fontSize: "16px",
                }}
            >
                <input
                    type="checkbox"
                    checked={polyglotModeEnabled}
                    onChange={(event) => {
                        if (event.target.checked) {
                            requestPolyglotLandscape();
                        }
                        setPolyglotModeEnabled(event.target.checked);
                    }}
                />
                Polyglot
            </label>
            {polyglotModeEnabled ? (
                <details
                    style={{
                        position: "relative",
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "16px",
                    }}
                >
                    <summary
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "116px",
                            height: "34px",
                            padding: "0 10px",
                            borderRadius: "8px",
                            border: "1px solid rgba(255,255,255,0.35)",
                            background: "rgba(0,0,0,0.22)",
                            cursor: "pointer",
                            listStyle: "none",
                        }}
                    >
                        {activePolyglotLanguageSet.size} langs
                    </summary>
                    <div
                        style={{
                            position: "absolute",
                            top: "calc(100% + 6px)",
                            right: 0,
                            zIndex: 20,
                            display: "grid",
                            gridTemplateColumns: "repeat(2, minmax(112px, 1fr))",
                            gap: "8px",
                            minWidth: "250px",
                            maxHeight: "min(56vh, 360px)",
                            overflowY: "auto",
                            padding: "12px",
                            borderRadius: "10px",
                            background: "rgba(10, 14, 26, 0.96)",
                            border: "1px solid rgba(255,255,255,0.16)",
                            boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
                        }}
                    >
                        {POLYGLOT_LANGUAGE_OPTIONS.map((languageCode) => {
                            const isSelected = activePolyglotLanguageSet.has(languageCode);
                            const isLocked = languageCode === "en" ||
                                (isSelected && activePolyglotLanguageSet.size <= MIN_POLYGLOT_LANGUAGE_COUNT);

                            return (
                                <label
                                    key={languageCode}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        color: "#ffffff",
                                        opacity: isLocked && languageCode !== "en" ? 0.65 : 1,
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        disabled={isLocked}
                                        onChange={() => handlePolyglotLanguageToggle(languageCode)}
                                    />
                                    {getLanguageName(languageCode)}
                                </label>
                            );
                        })}
                    </div>
                </details>
            ) : null}
        </div>
        {menuOpen ? (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    minWidth: "220px",
                    padding: "14px 16px",
                    borderRadius: "14px",
                    background: "rgba(10, 14, 26, 0.86)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
                }}
            >
                <label
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        color: "#ffffff",
                        fontWeight: "bold",
                    }}
                >
                    <input
                        type="checkbox"
                        checked={allCategoriesMode}
                        onChange={handleAllCategoriesToggle}
                    />
                    All Categories
                </label>
                {categoryGroups.map((category) => (
                    <details
                        key={category.id}
                        className="category-group"
                        style={{
                            borderTop: "1px solid rgba(255,255,255,0.12)",
                            paddingTop: "10px",
                        }}
                    >
                        <summary
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                color: "#ffffff",
                                cursor: "pointer",
                                fontWeight: "bold",
                                listStyle: "none",
                            }}
                        >
                            <span
                                className="category-group-toggle"
                                aria-hidden="true"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "18px",
                                    height: "18px",
                                    borderRadius: "4px",
                                    border: "1px solid rgba(255,255,255,0.35)",
                                    fontSize: "14px",
                                    lineHeight: 1,
                                }}
                            />
                            <input
                                type="checkbox"
                                checked={isCategoryGroupSelected(category)}
                                ref={(input) => {
                                    if (input) {
                                        input.indeterminate = isCategoryGroupPartlySelected(category);
                                    }
                                }}
                                onClick={(event) => event.stopPropagation()}
                                onChange={() => handleCategoryGroupToggle(category)}
                            />
                            {category.name}
                        </summary>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                                padding: "10px 0 2px 28px",
                            }}
                        >
                            {category.children.map((child) => (
                                <label
                                    key={child.id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        color: "#ffffff",
                                        fontWeight: "bold",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedCategories[child.id]}
                                        onChange={() => handleCategoryToggle(child.id)}
                                    />
                                    {getCategoryLabel(child.id)}
                                </label>
                            ))}
                        </div>
                    </details>
                ))}
            </div>
        ) : null}
        <Columns 
            key={state.languageData.map((language) => language.code).join("-")}
            languages={languageCodes}
            languageData={state.languageData}
            wordMatchDictionary={state.wordMatchDictionary}
            hintsEnabled={hintsEnabled}
            soundsEnabled={soundsEnabled}
            sendDataFromColumnsToApp={getGameState}
        />
        <div
            style={{
                marginTop: "10px",
                color: "rgba(255,255,255,0.88)",
                fontSize: "14px",
                fontWeight: 600,
                letterSpacing: "0.02em",
                textAlign: "center",
            }}
        >
            Round mix: {currentRoundMix.known} known, {currentRoundMix.learning} learning, {currentRoundMix.unknown} unknown
        </div>
        <div
            style={{
                color: "rgba(255,255,255,0.86)",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: 1.35,
                textAlign: "center",
                maxWidth: "720px",
            }}
        >
            Progress: {progressSummary.totalSeen} seen, {progressSummary.known} known, {progressSummary.learning} learning, {progressSummary.unknown} new | {progressSummary.correct} correct{totalAttempts > 0 ? `, ${accuracy}% accuracy` : ""}
        </div>
    </div>
);

};

export default ColumnsWrapper;
